import { BehaviorSubject } from "rxjs";
import { environment } from "src/environments/environment";
import { Product } from "./product.model";
import { ProductBuilder } from "./product.builder";
import { Venue } from "../venue/venue.model";
import { ProductType } from "./enums/product-type.enum";
import { ScheduleType } from "./enums";
import { CalendarEvent } from "../event/event.model";
import * as moment from "moment-timezone";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { ToastService } from "ng-uikit-pro-standard";
import { catchError } from "rxjs/operators";
import { AuthService } from "../services";

export class Pass {
  constructor(public event: CalendarEvent, public product: Product) {}

  async refresh(
    httpClient: HttpClient, 
    httpHeaders: HttpHeaders,
    toast: ToastService,
  ): Promise<Pass> {
    this.product = await this.product.refresh(httpClient, httpHeaders, toast);
    this.event = await this.event.refresh(httpClient, httpHeaders, this.product.id, toast);
    return new Pass(this.event, this.product);
  }
  isVisible(): boolean {
    return this.event.isVisible(this.product);
  }

  get isVis(): boolean {
    return this.event.isVisible(this.product);
  }

  get price(): number {
    return this.event.isCustomEvent
      ? this.event.inventory.price
      : this.product.price;
  }

  get sold(): number {
    return this.event.sold;
  }
  get max(): number {
    return this.event.inventory.max == null
      ? this.product.inventory.max == null
        ? 1000
        : this.product.inventory.max
      : this.event.inventory.max;
  }

  get name(): string {
    return this.product.shared.name;
  }
  get rank(): number {
    return this.event.rank;
  }
}

export class ProductFactory {
  public products: Product[];

  public products$: BehaviorSubject<Product[]>;
  public passes$: BehaviorSubject<Product[]>;

  public builder: ProductBuilder;
  public itemsPerPageOptions: number[] = [10, 25, 50];
  public productQuery: ProductQuery;
  public _begin: Date;
  public _end: Date;
  public searchText: string = "";

  constructor(
    public venue: Venue, 
    private httpClient: HttpClient,
    private authService: AuthService,
    public toast: ToastService,
  ) {
    this.products$ = new BehaviorSubject([]);
    this.passes$ = new BehaviorSubject([]);
    this._start();
  }
  public _start() {
    this.productQuery = new ProductQuery();
    this._begin = moment()
      .tz(this.venue.cityTimezone)
      .startOf("M")
      .toDate();
    this._end = moment()
      .tz(this.venue.cityTimezone)
      .endOf("M")
      .add(6, "M")
      .toDate();

    this._fetch();
  }

  public refresh() {
    this._fetch();
  }
  public async _fetch() {
    let getParams: any = {
        showHidden: this.productQuery.showHidden,
    }
    if (this.productQuery.scheduleType !== null) {
        getParams = {
            ...getParams,
            scheduleType: this.productQuery.scheduleType,
        }
    }
    if (this.productQuery.productType !== null) {
        getParams = {
            ...getParams,
            productType: this.productQuery.productType,
        }
    }

    const userJWT = await this.authService.user.getIdToken();

    this.httpClient.get<Product[]>(`${environment.apiBaseUrl}/api/v1/products`, {
        headers: new HttpHeaders().set("Authorization", `${userJWT}`),
        params: {
          venueId: this.venue.id,
          ...getParams,
        },
      }).subscribe((productsResponse) => {
        const products = productsResponse
          .map(productResponse => new ProductBuilder().loadAPIResponse(productResponse).build());
        this.products$.next(products);
        catchError((err) => {
            throw(err);
        })
      });

      this.httpClient.get<Product[]>(`${environment.apiBaseUrl}/api/v1/products`, {
        headers: new HttpHeaders().set("Authorization", `${userJWT}`),
        params: {
          venueId: this.venue.id,
          ...getParams,
        },
      }).subscribe((productsResponse) => {
        const products = productsResponse
          .map(productResponse => {
            let product = new ProductBuilder().loadAPIResponse(productResponse).build();
            product.initialize(this.httpClient, this.authService, this.toast, this._begin, this._end);
            return product;
          });
        this.passes$.next(products);
        catchError((err) => {
            throw(err);
        })
      });
  }

  public clean() {
    this.builder = null;
    this.products = [];
  }

  public create() {
    this.builder = new ProductBuilder();
  }

  public fix(product: Product) {
    this.builder = new ProductBuilder().edit(product);
  }

  public async setProductTypeFilter(productType: ProductType) {
    this.productQuery.setProduct(productType);
    await this._fetch();
    this.productQuery.setItemsPerPage(this.productQuery.itemsPerPage);
  }

  public async setScheduleTypeFilter(scheduleType: ScheduleType) {
    this.productQuery.setSchedule(scheduleType);
    await this._fetch();
    this.productQuery.setItemsPerPage(this.productQuery.itemsPerPage);
  }

  public async setHiddenFilter(bool: boolean) {
    this.productQuery.showHidden = bool;
    await this._fetch();
    this.productQuery.setItemsPerPage(this.productQuery.itemsPerPage);
  }
}

export class ProductQuery {
  public productType: ProductType;
  public scheduleType: ScheduleType;
  public showHidden: boolean;
  public itemsPerPage: number;
  public begin: number;
  public end: number;
  public totalResults: number;

  constructor() {
    this._default();
  }


  setTotal(n: number) {
    this.totalResults = n;
  }

  private _default() {
    this.productType = null;
    this.scheduleType = null;
    this.showHidden = false;
    this.itemsPerPage = 10;
    this.begin = 0;
    this.totalResults = 0;
    this.end = 10;
  }

  public setItemsPerPage(i: number) {
    this.itemsPerPage = i;
    this.begin = 0;
    this.end = i;
  }

  public nextPage() {
    this.begin += this.itemsPerPage;
    this.end += this.itemsPerPage;
    if (this.end > this.totalResults) this.end = this.totalResults;
  }

  public prevPage() {
    this.end -= this.itemsPerPage;
    this.begin -= this.itemsPerPage;
    if (this.begin < 0) this.begin = 0;
  }
  public setProduct(product: ProductType) {
    this.productType = product;
  }
  public setSchedule(schedule: ScheduleType) {
    this.scheduleType = schedule;
  }
}
